import React, { useState, useEffect } from 'react';
import { 
  Box, Paper, Typography, Grid, TextField, Button, MenuItem, Table, TableBody, 
  TableCell, TableHead, TableRow, Chip, IconButton, InputAdornment, TablePagination, 
  ToggleButtonGroup, ToggleButton, Avatar, Card, CardContent, CardActions, Badge, 
  Popover, Checkbox, FormControlLabel, Tooltip, Divider 
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import EditIcon from '@mui/icons-material/Edit';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FilterListIcon from '@mui/icons-material/FilterList';
import api from '../../api';
import StackedCell from './components/StackedCell';
import AdvancedFilterDialog from './components/AdvancedFilterDialog';
import ExitModal from './components/ExitModal';

const EmployeesList = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [attendanceMap, setAttendanceMap] = useState({});
  
  // Master Data for ID-to-Name Resolution & Filtering
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [eduLevels, setEduLevels] = useState([]);
  const [expRanges, setExpRanges] = useState([]);
  
  // NEW: State for Skills, Certs, and Languages
  const [skillsMaster, setSkillsMaster] = useState([]);
  const [certsMaster, setCertsMaster] = useState([]);
  const [langsMaster, setLangsMaster] = useState([]);
  
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ACTIVE');
  const [viewMode, setViewMode] = useState('grid');
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [photoStamp] = useState(Date.now());

  // --- DYNAMIC COLUMNS STATE ---
  const [anchorElCol, setAnchorElCol] = useState(null);
  const [cols, setCols] = useState({
    profile: true, name_id: true, desig_dept: true, managers: true, doj: true, status: true, action: true,
    personal_email: false, official_email: false, emp_level: false, dob: false, gender: false, mobile: false, 
    location: false, education: false, experience: false, skills: false, certifications: false, languages: false, 
    pan: false, aadhaar: false, father_name: false, mother_name: false, blood_group: false, address: false
  });

  // --- ADVANCED FILTER STATE ---
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [adv, setAdv] = useState({
    keyword: '', department_id: '', designation_id: '', emp_level: '', 
    leave_approver_l1: '', leave_approver_l2: '', doj_from: '', doj_to: '', 
    dob_from: '', dob_to: '', gender: '', blood_group: '', education_id: '', experience_id: '',
    skill: '', certification: '', language: '' // NEW: Added to state tracking
  });

  // --- EXIT MODAL STATE ---
  const [exitModalOpen, setExitModalOpen] = useState(false);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [exitReasons, setExitReasons] = useState([]);
  const [exitForm, setExitForm] = useState({ status: '', reason_id: '', exit_date: '', details: '' });

  useEffect(() => {
    fetchEmployees();
    
    // Fetch Master Data for Name Resolutions & Filters
    Promise.all([
      api.get('/settings/departments').catch(() => ({ data: [] })),
      api.get('/settings/designations').catch(() => ({ data: [] })),
      api.get('/settings/education').catch(() => ({ data: [] })), 
      api.get('/settings/experience').catch(() => ({ data: [] })),
      api.get('/settings/skills').catch(() => ({ data: [] })),          // NEW
      api.get('/settings/certifications').catch(() => ({ data: [] })),   // NEW
      api.get('/settings/languages').catch(() => ({ data: [] }))         // NEW
    ]).then(responses => {
      setDepartments(responses[0].data || []);
      setDesignations(responses[1].data || []);
      setEduLevels(responses[2].data || []);
      setExpRanges(responses[3].data || []);
      setSkillsMaster(responses[4].data || []);                          // NEW
      setCertsMaster(responses[5].data || []);                           // NEW
      setLangsMaster(responses[6].data || []);                           // NEW
    });

    const todayIST = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date());
    api.get(`/directory/live-status/global?report_date=${todayIST}`).then(res => {
      const liveMap = {};
      if (Array.isArray(res.data)) res.data.forEach(row => { liveMap[row.emp_id] = row.live_status; });
      setAttendanceMap(liveMap);
    }).catch(() => console.log("Live attendance map could not be loaded."));
  }, []);

  const fetchEmployees = () => api.get('/employees').then(res => setEmployees(res.data)).catch(console.error);

  const handlePageChange = (e, newPage) => setPage(newPage);
  const handleRowsChange = (e) => { 
    setRowsPerPage(parseInt(e.target.value, 10)); 
    setPage(0); 
  };

  const deptMap = departments.reduce((acc, d) => ({...acc, [d.id]: d.name}), {});
  const desigMap = designations.reduce((acc, d) => ({...acc, [d.id]: d.name}), {});
  const eduMap = eduLevels.reduce((acc, e) => ({...acc, [e.id]: e.name}), {});
  const expMap = expRanges.reduce((acc, e) => ({...acc, [e.id]: e.name}), {});
  const empMap = employees.reduce((acc, e) => ({...acc, [e.emp_id]: `${e.first_name || ''} ${e.last_name || ''}`.trim()}), {});

  const formatArray = (arr) => {
    if (!arr) return 'N/A';
    if (Array.isArray(arr)) return arr.join(', ');
    if (typeof arr === 'string') {
      try {
        const parsed = JSON.parse(arr);
        if (Array.isArray(parsed)) return parsed.join(', ');
      } catch(e) {}
      return arr;
    }
    return String(arr);
  };

  // --- FILTERING ENGINE ---
  const filteredEmployees = employees.filter(emp => {
    if (statusFilter !== 'ALL' && emp.status !== statusFilter) return false;
    
    if (search) {
      const q = search.toLowerCase();
      const fullName = String(`${emp.first_name || ''} ${emp.last_name || ''}`).toLowerCase();
      const idStr = String(emp.emp_id || '').toLowerCase();
      if (!fullName.includes(q) && !idStr.includes(q)) return false;
    }

    if (adv.keyword) {
      const kw = adv.keyword.toLowerCase();
      const searchableStr = String(
        `${emp.email || ''} ${emp.official_email || ''} ${emp.mobile || ''} ${emp.location || ''} ${emp.address || ''} ${emp.pan_number || ''} ${emp.aadhaar_number || ''} ${formatArray(emp.skills)} ${formatArray(emp.certifications)} ${formatArray(emp.languages)}`
      ).toLowerCase();
      if (!searchableStr.includes(kw)) return false;
    }

    if (adv.department_id && emp.department_id !== adv.department_id) return false;
    if (adv.designation_id && emp.designation_id !== adv.designation_id) return false;
    if (adv.emp_level && emp.employee_level !== adv.emp_level) return false;
    if (adv.leave_approver_l1 && emp.leave_approver_l1 !== adv.leave_approver_l1) return false;
    if (adv.leave_approver_l2 && emp.leave_approver_l2 !== adv.leave_approver_l2) return false;
    if (adv.gender && emp.gender !== adv.gender) return false;
    if (adv.blood_group && emp.blood_group !== adv.blood_group) return false;
    if (adv.education_id && emp.education_id !== adv.education_id) return false;
    if (adv.experience_id && emp.experience_id !== adv.experience_id) return false;
    
    // NEW: Strict array string matching for Skills, Certs, and Languages
    if (adv.skill && !formatArray(emp.skills).toLowerCase().includes(adv.skill.toLowerCase())) return false;
    if (adv.certification && !formatArray(emp.certifications).toLowerCase().includes(adv.certification.toLowerCase())) return false;
    if (adv.language && !formatArray(emp.languages).toLowerCase().includes(adv.language.toLowerCase())) return false;
    
    if (adv.doj_from && emp.date_of_joining && new Date(emp.date_of_joining) < new Date(adv.doj_from)) return false;
    if (adv.doj_to && emp.date_of_joining && new Date(emp.date_of_joining) > new Date(adv.doj_to)) return false;
    if (adv.dob_from && emp.dob && new Date(emp.dob) < new Date(adv.dob_from)) return false;
    if (adv.dob_to && emp.dob && new Date(emp.dob) > new Date(adv.dob_to)) return false;

    return true;
  });

  const paginatedData = filteredEmployees.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const getPhotoUrl = (emp) => emp.profile_photo_path ? `/api/v1/employees/${emp.emp_id}/profile-picture?t=${photoStamp}` : null;
  const getBadgeColor = (emp_id) => {
    const status = attendanceMap[emp_id];
    return status === 'IN' ? '#4caf50' : status === 'OUT' ? '#f44336' : '#9e9e9e';
  };

  const handleOpenExitModal = (emp) => {
    setSelectedEmp(emp);
    setExitForm({ status: '', reason_id: '', exit_date: '', details: '' });
    setExitReasons([]);
    setExitModalOpen(true);
  };

  const handleStatusChange = async (e) => {
    const newStatus = e.target.value;
    setExitForm({ ...exitForm, status: newStatus, reason_id: '' });
    if (newStatus) {
      try {
        const res = await api.get(`/employee-exit/reasons?status=${newStatus}`);
        setExitReasons(res.data);
      } catch (err) { console.error("Failed to fetch reasons", err); }
    }
  };

  const submitExit = async () => {
    if (!exitForm.status || !exitForm.reason_id || !exitForm.exit_date) return alert("Please fill all mandatory fields.");
    const performed_by = `${localStorage.getItem('first_name') || ''} ${localStorage.getItem('last_name') || ''}`.trim() || 'System Admin';
    try {
      await api.post(`/employee-exit/${selectedEmp.emp_id}/process`, { ...exitForm, performed_by });
      setExitModalOpen(false);
      fetchEmployees(); 
    } catch (err) { alert("Failed to process exit."); }
  };

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      
      {/* TOP TOOLBAR */}
      <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexGrow: 1, flexWrap: 'wrap' }}>
          <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold', textTransform: 'uppercase', mr: 2 }}>Employee Directory</Typography>
          <TextField 
            size="small" placeholder="Quick Search Name/ID..." value={search} onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
            sx={{ minWidth: 220, bgcolor: '#fbfbfb' }} disabled={Boolean(adv.keyword)}
          />
          <TextField select size="small" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} sx={{ minWidth: 150, bgcolor: '#fbfbfb' }}>
            <MenuItem value="ACTIVE">Active Employees</MenuItem>
            <MenuItem value="INACTIVE">Inactive / Exited</MenuItem>
            <MenuItem value="RESIGNED">Resigned</MenuItem>
            <MenuItem value="NOTICE">Notice</MenuItem>
            <MenuItem value="ALL">All Employees</MenuItem>
          </TextField>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {viewMode === 'list' && (
            <>
              <Tooltip title="Advanced Filters">
                <IconButton color={adv.keyword || adv.department_id ? "secondary" : "primary"} onClick={() => setFilterModalOpen(true)} sx={{ bgcolor: '#f0f4ff' }}><FilterListIcon /></IconButton>
              </Tooltip>
              <Tooltip title="Manage Columns">
                <IconButton color="primary" onClick={(e) => setAnchorElCol(e.currentTarget)} sx={{ bgcolor: '#f0f4ff' }}><ViewColumnIcon /></IconButton>
              </Tooltip>
            </>
          )}

          <ToggleButtonGroup value={viewMode} exclusive onChange={(e, val) => val && setViewMode(val)} size="small" sx={{ bgcolor: 'white' }}>
            <ToggleButton value="grid"><ViewModuleIcon /></ToggleButton>
            <ToggleButton value="list"><ViewListIcon /></ToggleButton>
          </ToggleButtonGroup>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/employees/add')} disableElevation>Add Employee</Button>
        </Box>
      </Paper>

      {/* DYNAMIC COLUMNS POPOVER */}
      <Popover open={Boolean(anchorElCol)} anchorEl={anchorElCol} onClose={() => setAnchorElCol(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Box sx={{ p: 2, width: 450, maxHeight: 400, overflowY: 'auto' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Toggle List Columns</Typography>
          <Divider sx={{ mb: 1 }} />
          <Grid container spacing={0}>
            {Object.keys(cols).map(key => (
              <Grid item xs={6} key={key}>
                <FormControlLabel 
                  control={<Checkbox size="small" checked={cols[key]} onChange={e => setCols({...cols, [key]: e.target.checked})} />} 
                  label={<Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{key.replace(/_/g, ' ')}</Typography>} 
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Popover>

      {/* PRO ADVANCED FILTER DIALOG */}
      <AdvancedFilterDialog 
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        adv={adv}
        setAdv={setAdv}
        departments={departments}
        designations={designations}
        employees={employees}
        eduLevels={eduLevels}
        expRanges={expRanges}
        skillsMaster={skillsMaster}    // NEW: Passed to child
        certsMaster={certsMaster}      // NEW: Passed to child
        langsMaster={langsMaster}      // NEW: Passed to child
      />

      {/* GRID VIEW */}
      {viewMode === 'grid' && (
        <Grid container spacing={2}>
          {paginatedData.map(emp => (
            <Grid item xs={12} sm={6} md={3} lg={2} key={emp.emp_id}>
              <Card elevation={2} sx={{ borderRadius: 2, display: 'flex', flexDirection: 'column', height: '100%', position: 'relative', '&:hover': { boxShadow: '0 6px 12px rgba(0,0,0,0.1)' } }}>
                <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: 2, pb: 1 }}>
                  <Badge overlap="circular" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} variant="dot" sx={{ '& .MuiBadge-badge': { backgroundColor: getBadgeColor(emp.emp_id), boxShadow: '0 0 0 2px white', width: 14, height: 14, borderRadius: '50%' } }}>
                    <Avatar src={getPhotoUrl(emp)} sx={{ width: 56, height: 56, mx: 'auto', bgcolor: 'primary.main', fontSize: '1.2rem' }}>
                      {!emp.profile_photo_path && `${emp.first_name?.charAt(0) || ''}${emp.last_name?.charAt(0) || ''}`}
                    </Avatar>
                  </Badge>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 1.5, lineHeight: 1.2 }}>{emp.first_name} {emp.last_name}</Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>{emp.emp_id} | {emp.role}</Typography>
                  <Chip size="small" label={emp.status} color={emp.status === 'ACTIVE' ? 'success' : 'default'} sx={{ mt: 0.5, height: 18, fontSize: '0.6rem', fontWeight: 'bold' }} />
                </CardContent>
                <CardActions sx={{ borderTop: '1px solid #f0f0f0', p: 0.5, bgcolor: '#fafafa', justifyContent: 'center' }}>
                  <Button size="small" sx={{ fontSize: '0.7rem' }} startIcon={<EditIcon sx={{ fontSize: '0.9rem' }} />} onClick={() => navigate(`/employees/edit/${emp.emp_id}`)}>View / Edit</Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* DYNAMIC LIST VIEW (Horizontal Scroll & Mapped Names) */}
      {viewMode === 'list' && (
        <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ overflowX: 'auto', width: '100%' }}>
            <Table size="small" sx={{ minWidth: 1000, whiteSpace: 'nowrap' }}>
              <TableHead sx={{ bgcolor: '#f0f4ff' }}>
                <TableRow>
                  {cols.profile && <TableCell sx={{ fontWeight: 'bold' }}>Profile</TableCell>}
                  {cols.name_id && <TableCell sx={{ fontWeight: 'bold' }}>Emp Name & ID</TableCell>}
                  {cols.desig_dept && <TableCell sx={{ fontWeight: 'bold' }}>Designation & Dept</TableCell>}
                  {cols.managers && <TableCell sx={{ fontWeight: 'bold' }}>L1 & L2 Manager</TableCell>}
                  {cols.emp_level && <TableCell sx={{ fontWeight: 'bold' }}>Level</TableCell>}
                  {cols.personal_email && <TableCell sx={{ fontWeight: 'bold' }}>Personal Email</TableCell>}
                  {cols.official_email && <TableCell sx={{ fontWeight: 'bold' }}>Official Email</TableCell>}
                  {cols.mobile && <TableCell sx={{ fontWeight: 'bold' }}>Mobile</TableCell>}
                  {cols.doj && <TableCell sx={{ fontWeight: 'bold' }}>DOJ</TableCell>}
                  {cols.dob && <TableCell sx={{ fontWeight: 'bold' }}>DOB</TableCell>}
                  {cols.gender && <TableCell sx={{ fontWeight: 'bold' }}>Gender</TableCell>}
                  {cols.blood_group && <TableCell sx={{ fontWeight: 'bold' }}>Blood Group</TableCell>}
                  {cols.location && <TableCell sx={{ fontWeight: 'bold' }}>Location</TableCell>}
                  {cols.education && <TableCell sx={{ fontWeight: 'bold' }}>Education</TableCell>}
                  {cols.experience && <TableCell sx={{ fontWeight: 'bold' }}>Experience</TableCell>}
                  {cols.skills && <TableCell sx={{ fontWeight: 'bold' }}>Skills</TableCell>}
                  {cols.certifications && <TableCell sx={{ fontWeight: 'bold' }}>Certifications</TableCell>}
                  {cols.languages && <TableCell sx={{ fontWeight: 'bold' }}>Languages</TableCell>}
                  {cols.pan && <TableCell sx={{ fontWeight: 'bold' }}>PAN</TableCell>}
                  {cols.aadhaar && <TableCell sx={{ fontWeight: 'bold' }}>Aadhaar</TableCell>}
                  {cols.father_name && <TableCell sx={{ fontWeight: 'bold' }}>Father's Name</TableCell>}
                  {cols.mother_name && <TableCell sx={{ fontWeight: 'bold' }}>Mother's Name</TableCell>}
                  {cols.address && <TableCell sx={{ fontWeight: 'bold' }}>Address</TableCell>}
                  {cols.status && <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>}
                  {cols.action && <TableCell align="center" sx={{ fontWeight: 'bold', position: 'sticky', right: 0, bgcolor: '#f0f4ff', zIndex: 1, boxShadow: '-2px 0 5px rgba(0,0,0,0.05)' }}>Action</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map(emp => (
                  <TableRow key={emp.emp_id} hover sx={{ '& > td': { py: 1.5 } }}>
                    {cols.profile && (
                      <TableCell>
                        <Badge overlap="circular" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} variant="dot" sx={{ '& .MuiBadge-badge': { backgroundColor: getBadgeColor(emp.emp_id), boxShadow: '0 0 0 2px white', width: 10, height: 10, borderRadius: '50%' } }}>
                          <Avatar src={getPhotoUrl(emp)} sx={{ width: 40, height: 40, bgcolor: 'primary.main', fontSize: '1rem' }}>{!emp.profile_photo_path && emp.first_name?.charAt(0)}</Avatar>
                        </Badge>
                      </TableCell>
                    )}
                    {cols.name_id && <TableCell><StackedCell topText={`${emp.first_name || ''} ${emp.last_name || ''}`} bottomText={emp.emp_id} /></TableCell>}
                    {cols.desig_dept && <TableCell><StackedCell topText={desigMap[emp.designation_id] || emp.role} bottomText={deptMap[emp.department_id] || 'N/A'} /></TableCell>}
                    {cols.managers && <TableCell><StackedCell topText={`L1: ${empMap[emp.leave_approver_l1] || 'N/A'}`} bottomText={`L2: ${empMap[emp.leave_approver_l2] || 'N/A'}`} /></TableCell>}
                    {cols.emp_level && <TableCell>{emp.employee_level || 'N/A'}</TableCell>}
                    {cols.personal_email && <TableCell sx={{ color: '#546e7a', fontSize: '0.85rem' }}>{emp.email || 'N/A'}</TableCell>}
                    {cols.official_email && <TableCell sx={{ color: '#546e7a', fontSize: '0.85rem' }}>{emp.official_email || 'N/A'}</TableCell>}
                    {cols.mobile && <TableCell sx={{ color: '#546e7a', fontSize: '0.85rem' }}>{emp.mobile || 'N/A'}</TableCell>}
                    {cols.doj && <TableCell sx={{ color: '#546e7a', fontSize: '0.85rem' }}>{emp.date_of_joining ? new Date(emp.date_of_joining).toLocaleDateString() : 'N/A'}</TableCell>}
                    {cols.dob && <TableCell sx={{ color: '#546e7a', fontSize: '0.85rem' }}>{emp.dob ? new Date(emp.dob).toLocaleDateString() : 'N/A'}</TableCell>}
                    {cols.gender && <TableCell>{emp.gender || 'N/A'}</TableCell>}
                    {cols.blood_group && <TableCell>{emp.blood_group || 'N/A'}</TableCell>}
                    {cols.location && <TableCell>{emp.location || 'N/A'}</TableCell>}
                    {cols.education && <TableCell>{eduMap[emp.education_id] || 'N/A'}</TableCell>}
                    {cols.experience && <TableCell>{expMap[emp.experience_id] || 'N/A'}</TableCell>}
                    {cols.skills && <TableCell>{formatArray(emp.skills)}</TableCell>}
                    {cols.certifications && <TableCell>{formatArray(emp.certifications)}</TableCell>}
                    {cols.languages && <TableCell>{formatArray(emp.languages)}</TableCell>}
                    {cols.pan && <TableCell>{emp.pan_number || 'N/A'}</TableCell>}
                    {cols.aadhaar && <TableCell>{emp.aadhaar_number || 'N/A'}</TableCell>}
                    {cols.father_name && <TableCell>{emp.father_name || 'N/A'}</TableCell>}
                    {cols.mother_name && <TableCell>{emp.mother_name || 'N/A'}</TableCell>}
                    {cols.address && <TableCell>{emp.address || 'N/A'}</TableCell>}
                    {cols.status && <TableCell><Chip size="small" label={emp.status} color={emp.status === 'ACTIVE' ? 'success' : 'default'} sx={{ fontWeight: 'bold' }} /></TableCell>}
                    {cols.action && (
                      <TableCell align="center" sx={{ position: 'sticky', right: 0, bgcolor: 'inherit', boxShadow: '-2px 0 5px rgba(0,0,0,0.05)' }}>
                        <IconButton size="small" color="primary" onClick={() => navigate(`/employees/edit/${emp.emp_id}`)} sx={{ bgcolor: '#f0f4ff', mr: 1 }}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => handleOpenExitModal(emp)} title="Process Exit" sx={{ bgcolor: '#ffebee' }}><ExitToAppIcon fontSize="small" /></IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      )}

      <Paper elevation={0} sx={{ mt: 2, bgcolor: 'transparent', display: 'flex', justifyContent: 'flex-end' }}>
        <TablePagination 
          component="div" 
          count={filteredEmployees.length} 
          page={page} 
          onPageChange={handlePageChange} 
          rowsPerPage={rowsPerPage} 
          onRowsPerPageChange={handleRowsChange} 
          rowsPerPageOptions={[12, 24, 48, 96]} 
        />
      </Paper>

      <ExitModal 
        open={exitModalOpen}
        onClose={() => setExitModalOpen(false)}
        selectedEmp={selectedEmp}
        exitForm={exitForm}
        setExitForm={setExitForm}
        handleStatusChange={handleStatusChange}
        submitExit={submitExit}
        desigMap={desigMap}
        deptMap={deptMap}
        exitReasons={exitReasons}
      />
    </Box>
  );
};

export default EmployeesList;